import type { RequestConfig } from "@app/config/axios/http";
import { boApi, type ApiOmpBoState } from "@src/api/bo";
import { divisionApi, type ApiDivision } from "@src/api/dictionaries/divisions";
import { enterpriseServices, type Enterprise } from "@src/api/dictionaries/enterprise";
import { measureApi, type ApiMeasure } from "@src/api/dictionaries/measures";
import { ordersApi, type ApiOrder } from "@src/api/orders";
import { stockDepotCardsApi, type ApiStockDepotCard } from "@src/api/stock/stockDepotCards";
import { stockObjsApi, type StockObj } from "@src/api/stock/stockObjs";
import { lotsServices, type ApiStockLot } from "../lots";
import {
    storePlacesApi,
    storePlacesServices,
    type ApiStorePlaceData,
    type SpMoving,
    type StorePlaceRemainder,
} from "../storePlaces";
import { onsideInvoicesApi } from "./api.core";
import type {
    ApiOnsideInvoiceContentData,
    ApiOnsideInvoiceData,
    OnsideInvoice,
    OnsideInvoiceContent,
    OnsideInvoiceContentConfirmLeaveParams,
    OnsideInvoiceContentExtends,
    OnsideInvoiceContentFilter,
    OnsideInvoiceExtends,
    OnsideInvoiceFilter,
} from "./types";

/** Сервисный слой — накладные на сторону и состав НС для отгрузки СГП. */
export const onsideInvoicesServices = {
    ...onsideInvoicesApi,

    /**
     * Загружает НС и при необходимости догружает склад, контрагента и состояние BO.
     * @param params фильтр и include
     * @param config опции HTTP-запроса
     */
    load: async (
        params: {
            filter?: OnsideInvoiceFilter;
            include?: Array<keyof OnsideInvoiceExtends>;
        },
        config?: RequestConfig,
    ): Promise<OnsideInvoice[]> => {
        const { filter, include } = params;
        const invoices = await onsideInvoicesApi.load({ filter }, config);

        if (include == null) {
            return invoices.map((invoice) => serializeInvoice(invoice));
        }

        const includeSender = include.includes("WsSender");
        const includeEnterprise = include.includes("Enterprise");
        const includeState = include.includes("State");
        const senderCodes = new Set<number>();
        const enterpriseCodes = new Set<number>();
        const stateCodes = new Set<number>();

        invoices.forEach((invoice) => {
            if (includeSender) addValidCode(senderCodes, invoice.WsSenderCode);
            if (includeEnterprise) addValidCode(enterpriseCodes, invoice.EnterpriseCode);
            if (includeState) addValidCode(stateCodes, invoice.Bo.State);
        });

        const senders = new Map<number, ApiDivision>();
        const enterprises = new Map<number, Enterprise>();
        const states = new Map<number, ApiOmpBoState>();
        const promises: Promise<unknown>[] = [];

        if (senderCodes.size > 0) {
            promises.push(
                divisionApi.load({ flt: { Codes: [...senderCodes] } }, config).then(({ items }) => {
                    items.forEach((item) => senders.set(item.Code, item));
                }),
            );
        }

        if (enterpriseCodes.size > 0) {
            promises.push(
                enterpriseServices.load({ flt: { Codes: [...enterpriseCodes] } }, config).then((items) => {
                    items.forEach((item) => enterprises.set(item.Code, item));
                }),
            );
        }

        if (stateCodes.size > 0) {
            promises.push(
                boApi.states.load({ Codes: [...stateCodes] }, config).then((items) => {
                    items.forEach((item) => states.set(item.Code, item));
                }),
            );
        }

        await Promise.all(promises);

        return invoices.map((invoice) =>
            serializeInvoice(invoice, {
                WsSender: includeSender ? senders.get(invoice.WsSenderCode) : undefined,
                Enterprise: includeEnterprise ? enterprises.get(invoice.EnterpriseCode) : undefined,
                State: includeState ? states.get(invoice.Bo.State) : undefined,
            }),
        );
    },

    /**
     * Возвращает одну НС по коду.
     * @param params код НС и include
     * @param config опции HTTP-запроса
     */
    loadByCode: async (
        params: {
            code: number;
            include?: Array<keyof OnsideInvoiceExtends>;
        },
        config?: RequestConfig,
    ): Promise<OnsideInvoice | null> => {
        const invoices = await onsideInvoicesServices.load(
            {
                filter: { Codes: [params.code] },
                include: params.include,
            },
            config,
        );
        return invoices[0] ?? null;
    },

    /** Состав НС. */
    content: {
        /**
         * Загружает строки состава НС и связанные справочники.
         * @param params фильтр и include состава
         * @param config опции HTTP-запроса
         */
        load: async (
            params: {
                filter?: OnsideInvoiceContentFilter;
                include?: Array<keyof OnsideInvoiceContentExtends>;
            },
            config?: RequestConfig,
        ): Promise<OnsideInvoiceContent[]> => {
            const { filter, include } = params;
            const contents = await onsideInvoicesApi.content.load({ filter }, config);

            if (include == null) {
                return contents.map((content) => serializeContent(content));
            }

            return enrichContents(contents, include, config);
        },

        /**
         * Устанавливает признак подтверждения отгрузки строки НС.
         * @param params код строки и значение `confirmLeave`
         * @param config опции HTTP-запроса
         */
        setConfirmLeave: async (params: OnsideInvoiceContentConfirmLeaveParams, config?: RequestConfig): Promise<void> => {
            await onsideInvoicesApi.content.setConfirmLeave(params, config);
        },
    },
} as const;

async function enrichContents(
    contents: ApiOnsideInvoiceContentData[],
    include: Array<keyof OnsideInvoiceContentExtends>,
    config?: RequestConfig,
): Promise<OnsideInvoiceContent[]> {
    const includeStockObj = include.includes("StockObj");
    const includeMeasure = include.includes("Measure");
    const includeLot = include.includes("Lot");
    const includeOrder = include.includes("Order");
    const includeDepotCard = include.includes("DepotCard");
    const includeSpMovings = include.includes("SpMovings");
    const includeRemainders = include.includes("PlacedCellRemainders");
    const stockObjCodes = new Set<number>();
    const measureCodes = new Set<number>();
    const lotCodes = new Set<number>();
    const orderCodes = new Set<number>();
    const depotCardCodes = new Set<number>();
    const leaveSdbCodes = new Set<number>();

    contents.forEach((content) => {
        if (includeStockObj) addValidCode(stockObjCodes, content.StockobjCode);
        if (includeMeasure) addValidCode(measureCodes, content.MeasCode);
        if (includeLot || includeRemainders) addValidCode(lotCodes, content.LotCode);
        if (includeOrder) addValidCode(orderCodes, content.OrderCode);
        if (includeDepotCard) addValidCode(depotCardCodes, content.StockCardCode);
        if (includeSpMovings) addValidCode(leaveSdbCodes, content.LeaveSdbCode);
    });

    const stockObjs = new Map<number, StockObj>();
    const measures = new Map<number, ApiMeasure>();
    const lots = new Map<number, ApiStockLot>();
    const orders = new Map<number, ApiOrder>();
    const depotCards = new Map<number, ApiStockDepotCard>();
    const spMovings = new Map<number, SpMoving[]>();
    const remainders = new Map<number, StorePlaceRemainder[]>();
    const promises: Promise<unknown>[] = [];

    if (stockObjCodes.size > 0) {
        promises.push(
            stockObjsApi.load({ flt: { Codes: [...stockObjCodes] } }, config).then((items) => {
                items.forEach((item) => stockObjs.set(item.Code, item));
            }),
        );
    }

    if (measureCodes.size > 0) {
        promises.push(
            measureApi.load({ flt: { Codes: [...measureCodes] } }, config).then((items) => {
                items.forEach((item) => measures.set(item.Code, item));
            }),
        );
    }

    if (lotCodes.size > 0) {
        promises.push(
            lotsServices.load({ flt: { Codes: [...lotCodes] } }, config).then((items) => {
                items.forEach((item) => lots.set(item.LotCode, item));
            }),
        );
    }

    if (orderCodes.size > 0) {
        promises.push(
            ordersApi.load({ flt: { Codes: [...orderCodes] } }, config).then((items) => {
                items.forEach((item) => orders.set(item.Code, item));
            }),
        );
    }

    if (depotCardCodes.size > 0) {
        promises.push(
            stockDepotCardsApi.load({ flt: { Codes: [...depotCardCodes] } }, config).then((items) => {
                items.forEach((item) => depotCards.set(item.Code, item));
            }),
        );
    }

    if (leaveSdbCodes.size > 0) {
        promises.push(
            storePlacesApi.spMoving
                .loadByFilter(
                    {
                        filter: {
                            SdbCodes: [...leaveSdbCodes],
                            OnlyNotInv: true,
                        },
                    },
                    config,
                )
                .then(async (items) => {
                    items.forEach((item) => {
                        const itemsBySdb = spMovings.get(item.SdbCode) ?? [];
                        itemsBySdb.push(item);
                        spMovings.set(item.SdbCode, itemsBySdb);
                    });

                    if (includeSpMovings) {
                        await enrichSpMovingsWithStorePlaces(spMovings, config);
                    }
                }),
        );
    }

    if (includeRemainders && lotCodes.size > 0) {
        promises.push(
            storePlacesServices.remainders
                .load(
                    {
                        filter: {
                            LotCodes: [...lotCodes],
                            InContextOfLot: true,
                            OnlyPositiveRemainders: true,
                        },
                        include: ["StorePlace"],
                    },
                    config,
                )
                .then((items) => {
                    items.forEach((item) => {
                        if (!isValidCode(item.LotCode)) {
                            return;
                        }

                        const itemsByLot = remainders.get(item.LotCode) ?? [];
                        itemsByLot.push(item);
                        remainders.set(item.LotCode, itemsByLot);
                    });
                }),
        );
    }

    await Promise.all(promises);

    return contents.map((content) =>
        serializeContent(content, {
            StockObj: includeStockObj ? stockObjs.get(content.StockobjCode) : undefined,
            Measure: includeMeasure ? measures.get(content.MeasCode) : undefined,
            Lot: includeLot ? lots.get(content.LotCode) : undefined,
            Order: includeOrder ? orders.get(content.OrderCode) : undefined,
            DepotCard: includeDepotCard ? depotCards.get(content.StockCardCode) : undefined,
            SpMovings: includeSpMovings ? spMovings.get(content.LeaveSdbCode) : undefined,
            PlacedCellRemainders: includeRemainders ? remainders.get(content.LotCode) : undefined,
        }),
    );
}

function serializeInvoice(invoice: ApiOnsideInvoiceData, includeData?: Partial<OnsideInvoiceExtends>): OnsideInvoice {
    return {
        ...invoice,
        WsSender: includeData?.WsSender,
        Enterprise: includeData?.Enterprise,
        State: includeData?.State,
    };
}

function serializeContent(
    content: ApiOnsideInvoiceContentData,
    includeData?: Partial<OnsideInvoiceContentExtends>,
): OnsideInvoiceContent {
    return {
        ...content,
        ConfirmLeave: content.ConfirmLeave,
        LotSoNum: content.LotSoNum,
        StockObj: includeData?.StockObj,
        Measure: includeData?.Measure,
        Lot: includeData?.Lot,
        Order: includeData?.Order,
        DepotCard: includeData?.DepotCard,
        SpMovings: includeData?.SpMovings,
        PlacedCellRemainders: includeData?.PlacedCellRemainders,
    };
}

async function enrichSpMovingsWithStorePlaces(spMovings: Map<number, SpMoving[]>, config?: RequestConfig): Promise<void> {
    const storePlaceCodes = new Set<number>();

    spMovings.forEach((movements) => {
        movements.forEach((movement) => {
            if (isValidCode(movement.SpCode)) {
                storePlaceCodes.add(movement.SpCode);
            }
        });
    });

    if (storePlaceCodes.size === 0) {
        return;
    }

    const storePlaces = new Map<number, ApiStorePlaceData>();
    const loaded = await storePlacesApi.load({ filter: { Codes: [...storePlaceCodes] } }, config);
    loaded.forEach((storePlace) => storePlaces.set(storePlace.Code, storePlace));

    spMovings.forEach((movements, sdbCode) => {
        spMovings.set(
            sdbCode,
            movements.map((movement) => ({
                ...movement,
                StorePlace: isValidCode(movement.SpCode) ? storePlaces.get(movement.SpCode) : undefined,
            })),
        );
    });
}

function addValidCode(target: Set<number>, code: number | null | undefined): void {
    if (isValidCode(code)) {
        target.add(code);
    }
}

function isValidCode(code: number | null | undefined): code is number {
    return code != null && code !== 0;
}
